import { ItemBiddingClientChangeFeed } from "@hds-products/event-runtime-entities-client";
import { useAdminItemBiddingStore } from '../pinia/adminItemBiddingStore';
import { useAdminSdkStatusStore } from '../pinia/adminSdkStatusStore';
import { compare } from 'fast-json-patch';

class AdminItemBiddingPiniaVueAdapter {
    #_changeFeed;
    #_deltaServerClient;
    #_itemBiddingPiniaStore;
    #_sdkStatusPiniaStore;
    #_itemWatchers = new Map();
    #_eventDetailIdToWatcherIdMap = new Map();

    constructor(deltaServerClient, itemBiddingBaseUri, customHeaderFn) {
        this.#_deltaServerClient = deltaServerClient;
        this.#_changeFeed = new ItemBiddingClientChangeFeed(deltaServerClient, itemBiddingBaseUri, customHeaderFn, null);
        this.#_itemBiddingPiniaStore = useAdminItemBiddingStore();
        this.#_sdkStatusPiniaStore = useAdminSdkStatusStore();
        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-item-bidding', { title: 'Item Bidding', description: 'Live bid stream and item auction state', status: 'INITIALIZING' })
        this.onItemBiddingAdded = this.onItemBiddingAdded.bind(this);
        this.onItemBiddingUpdated = this.onItemBiddingUpdated.bind(this);
        this.onItemBiddingRemoved = this.onItemBiddingRemoved.bind(this);
    }
    async _connectAndStartChangeFeed(isRestart = false) {
        let attempts = 1;
        if (this.#_deltaServerClient.isConnected()) {
            await this.#_changeFeed.whenStarted();
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-item-bidding', { status: 'CONNECTED', attempts })
        } else {
            while (!this.#_deltaServerClient.isConnected()) {
                try {
                    await this.#_deltaServerClient.whenConnected();
                    if (attempts === 1) {
                        console.info(`admin-item-bidding changefeed connected`);
                    } else {
                        console.info(`admin-item-bidding changefeed connected after attempt #${attempts}.`);
                    }
                    if (isRestart) {
                        await this.#_changeFeed.restart();
                    } else {
                        await this.#_changeFeed.whenStarted();
                    }
                    attempts = 1;
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-item-bidding', { status: 'CONNECTED', attempts })
                } catch (err) {
                    //A refused principal stays refused, so another attempt cannot succeed
                    if (err?.closeCode === 4403) {
                        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-item-bidding', { status: 'DISCONNECTED', attempts })
                        return;
                    }
                    attempts++;
                    console.error(`Attempt #${attempts}: admin-item-bidding changefeed error: ${err}`);
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-item-bidding', { status: 'DISCONNECTED', attempts })
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
        }
    }
    async initialize() {
        this.#_changeFeed.addFailureListener(async (error) => {
            console.error(`Change Feed Failure (AdminItemBiddingChangeFeed) => ${error.message}`);
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-item-bidding', { status: 'DISCONNECTED' })
            await this._connectAndStartChangeFeed(true);
        }, this);
        await this._connectAndStartChangeFeed();
    }
    async watchItemsByEventDetailId(eventDetailId) {
        const watcher = await this.#_changeFeed.watchByProperties({ eventDetailId });
        watcher.on('entity-added', this.onItemBiddingAdded);
        watcher.on('entity-updated', this.onItemBiddingUpdated);
        watcher.on('entity-removed', this.onItemBiddingRemoved);
        this.#_eventDetailIdToWatcherIdMap.set(eventDetailId, watcher.watchId);
        watcher.entities?.forEach((value, key) => {
            this.onItemBiddingAdded(value);
        })
    }
    async unwatchItemsByEventDetailId(eventDetailId) {
        const watcherId = this.#_eventDetailIdToWatcherIdMap.get(eventDetailId);
        const watcher = this.#_changeFeed.getWatcher(watcherId);
        watcher.off('entity-added', this.onItemBiddingAdded);
        watcher.off('entity-updated', this.onItemBiddingUpdated);
        watcher.off('entity-removed', this.onItemBiddingRemoved);
        watcher.entities?.forEach((value, key) => {
            this.onItemBiddingRemoved(value.id);
        })
        this.#_changeFeed.unwatch(watcherId);
        this.#_eventDetailIdToWatcherIdMap.delete(eventDetailId);
    }
    async watchItem(itemId) {
        if (this.#_itemWatchers.has(itemId)) { return; }
        const watcher = await this.#_changeFeed.watch(itemId);
        watcher.on('entity-added', this.onItemBiddingAdded);
        watcher.on('entity-updated', this.onItemBiddingUpdated);
        watcher.on('entity-removed', this.onItemBiddingRemoved);
        this.#_itemWatchers.set(itemId, watcher);
        this.onItemBiddingAdded(watcher.getEntity(itemId));
    }
    async unwatchItem(itemId) {
        if (!this.#_itemWatchers.has(itemId)) { return; }
        const watcher = this.#_itemWatchers.get(itemId);
        watcher.off('entity-added', this.onItemBiddingAdded);
        watcher.off('entity-updated', this.onItemBiddingUpdated);
        watcher.off('entity-removed', this.onItemBiddingRemoved);
        const item = watcher.getEntity(itemId)
        this.onItemBiddingRemoved(item.id);
        this.#_changeFeed.unwatch(watcher.watchId);
        this.#_itemWatchers.delete(itemId);
    }
    onItemBiddingAdded(itemBiddingEntity) {
        this.#_itemBiddingPiniaStore.addItemBidding(itemBiddingEntity.toJSON());
    }
    onItemBiddingUpdated(itemBiddingId, preModel, newEntity) {
        const jsonPatch = compare(preModel, newEntity.toJSON());
        if (jsonPatch.length > 0) {
            this.#_itemBiddingPiniaStore.applyJsonPatch(itemBiddingId, jsonPatch);
        }
    }
    onItemBiddingRemoved(itemBiddingId) {
        this.#_itemBiddingPiniaStore.removeItemBidding(itemBiddingId);
    }
}

export { AdminItemBiddingPiniaVueAdapter }
export default AdminItemBiddingPiniaVueAdapter;